import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Spinner } from '../../../components/LogoLoader';

export interface Notice {
    id: string;
    text: string;
    type?: 'default' | 'syncing' | 'success' | 'error' | 'warning' | 'ai-processing';
    imageUrl?: string;
    onAction?: () => void;
    actionType?: string;
    actionPayload?: any;
    sticky?: boolean;
}

interface StackedNoticeBannersProps {
    isVisible?: boolean;
    notices: Notice[];
    onDelete: (index: number) => void;
    onExpand: (index: number) => void;
}

export const StackedNoticeBanners: React.FC<StackedNoticeBannersProps> = ({
    isVisible = true,
    notices,
    onDelete,
    onExpand,
}) => {
    const [renderList, setRenderList] = useState<Notice[]>([]);
    const noticesRef = useRef(notices);

    useEffect(() => {
        noticesRef.current = notices;
    }, [notices]);

    useEffect(() => {
        if (!isVisible) {
            setRenderList([]);
            return;
        }

        if (notices.length === 0) {
            setRenderList([]);
            return;
        }

        // Fresh show: Stagger existing notices onto the screen quickly
        if (renderList.length === 0) {
            let i = notices.length - 1; 

            // Show first item immediately for instant feedback
            const firstItem = notices[i];
            if (firstItem) {
                setRenderList([firstItem]);
                i--;
            }

            // Stagger the rest of the existing items
            if (i >= 0) {
                const timer = setInterval(() => {
                    const currentPool = noticesRef.current;
                    if (i >= 0 && i < currentPool.length) {
                        const item = currentPool[i];
                        if (item) {
                            setRenderList(prev => [item, ...prev]);
                        }
                        i--;
                        // If that was the last one, clear immediately to avoid "dead" tick
                        if (i < 0) clearInterval(timer);
                    } else {
                        clearInterval(timer);
                    }
                }, 120); 
                return () => clearInterval(timer);
            }
        } else {
            // Live updates (new notices arriving or items deleted or text updated)
            setRenderList(notices);
        }
    }, [isVisible, notices]);

    return (
        <div className="absolute top-2 left-2 md:left-4 z-[40] pointer-events-none flex flex-col gap-1.5 w-80">
            <AnimatePresence initial={true}>
                {renderList.map((notice) => {
                    const originalIndex = notices.findIndex(n => n?.id === notice.id);
                    if (originalIndex === -1) return null;

                    return (
                        <motion.div
                            key={notice.id}
                            layout
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.1 } }}
                            transition={{
                                type: "spring",
                                stiffness: 600,
                                damping: 45,
                                mass: 0.4
                            }}
                            whileHover={{}}
                            className={`
                                pointer-events-auto
                                bg-white dark:bg-neutral-900/95 backdrop-blur-md border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-xl
                                p-3 flex flex-col cursor-pointer w-full
                                hover:bg-neutral-50 dark:hover:bg-neutral-800/90 transition-colors
                            `}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (notice.onAction) {
                                    notice.onAction();
                                } else if (!notice.sticky) {
                                    onExpand(originalIndex);
                                }
                            }}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border overflow-hidden ${
                                        notice.imageUrl
                                        ? 'bg-transparent border-neutral-100 dark:border-neutral-800 p-1.5'
                                        : notice.type === 'syncing' ? 'bg-accent-primary/5 border-accent-primary/10'
                                        : notice.type === 'success' ? 'bg-success-500/10 border-success-500/20'
                                        : notice.type === 'error' ? 'bg-error-500/10 border-error-500/20'
                                        : 'bg-accent-primary/10 border-accent-primary/20'
                                    }`}>
                                        {notice.type === 'syncing' ? (
                                            <Spinner size="xs" className="text-accent-primary" />
                                        ) : notice.type === 'success' ? (
                                            notice.imageUrl ? (
                                                <img src={notice.imageUrl} className="w-full h-full object-contain" alt="" />
                                            ) : (
                                                <svg className="w-4 h-4 text-success-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M20 6L9 17l-5-5" />
                                                </svg>
                                            )
                                        ) : notice.type === 'error' ? (
                                            notice.imageUrl ? (
                                                <img src={notice.imageUrl} className="w-full h-full object-contain" alt="" />
                                            ) : (
                                                <svg className="w-4 h-4 text-error-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <line x1="12" y1="8" x2="12" y2="12" />
                                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                                </svg>
                                            )
                                        ) : notice.imageUrl ? (
                                            <img src={notice.imageUrl} className="w-full h-full object-contain" alt="" />
                                        ) : (
                                            <svg className="w-4 h-4 text-accent-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                                            </svg>
                                        )}
                                    </div>
                                    <p className="text-neutral-900 dark:text-white text-sm font-medium leading-snug line-clamp-2">
                                        {notice.text}
                                    </p>
                                </div>

                                {!notice.sticky && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(originalIndex);
                                        }}
                                        className="p-1.5 bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-800 rounded-full text-neutral-400 dark:text-white/40 hover:text-neutral-600 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors shrink-0"
                                        title="Dismiss"
                                    >
                                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M18 6L6 18M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
};
